// =============================================================================
// components/SectionErrorBoundary.js — Phase 82.7 (modular resilience)
//
// A small, presentational-only error boundary that isolates section-level
// failures inside a page. The problem it solves:
//
//   The new Settings hub has 8 sections (Plan, Family, Profile, Life Profile,
//   Region, Email, AI Usage, Help). The new Recurring hub has 3+ tabs.
//   Each section owns its own data hook, its own network calls, and its own
//   rendering surface — but they all live inside the SAME React tree. Before
//   this boundary, an unhandled error in, say, Plan (a bad invoice row shape,
//   a broken date) would blow up the entire Settings page and leave the user
//   stranded on a blank screen.
//
// With <SectionErrorBoundary /> wrapping each section:
//   • The error is caught locally.
//   • Sibling sections continue to render normally.
//   • The user sees a compact, calm fallback with a "Reload this section"
//     button that lets them retry without leaving the page.
//   • The error is console.error'd (with the section label prefix) so it
//     shows up in normal debugging paths.
//
// Design constraints:
//   • Pure React (no external libs) — a class component is required because
//     hooks don't support componentDidCatch.
//   • Dark-mode aware via token classes.
//   • Never surfaces the raw error message to the UI (could contain PII or
//     backend paths). Detail lives in the console only.
//   • Retry is best-effort: bumping a `retryKey` re-mounts the subtree so
//     data hooks re-run from scratch.
// =============================================================================
import React from 'react';

export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log to console with a clear label so it's easy to grep for in support.
    console.error(`[SectionErrorBoundary:${this.props.label || 'section'}]`, error, info);
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, retryKey: s.retryKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="rounded-2xl border border-dashed border-border/70 bg-offwhite/40 px-6 py-8 text-center"
          role="alert"
          data-testid={`section-error-${(this.props.label || 'section').toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          <p className="text-sm font-medium text-charcoal">
            This section couldn&apos;t load right now.
          </p>
          <p className="text-xs text-muted-foreground mt-1 mb-4 max-w-md mx-auto leading-relaxed">
            Everything else on this page still works. Try reloading just this
            section &mdash; if the problem persists, let us know.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium border border-evergreen/30 text-evergreen hover:bg-evergreen/5 transition-colors"
          >
            Reload this section
          </button>
        </div>
      );
    }
    // The key forces a remount of the subtree when the user retries,
    // re-running any useEffects and re-fetching data.
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
